import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import type { AltAdminOverview, AltBatchProgress, AltBatchView, AltFileEdit, AltPublishResult } from '@agio/core/domain';
import { activeClientState, dataVersionState } from '../../state.js';
import { Card } from '../Card.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner, Spinner } from '../FormBits.js';
import { FlowSteps } from './FlowSteps.js';
import { AltReview, Notice, fmtDate } from './AltReview.js';
import { altApi } from './altApi.js';
import { fmtMoney } from '../../utils.js';

/**
 * השקעות אלטרנטיביות — quarterly reports of private equity, real estate, hedge
 * funds… in any form (PDF, a scan or photo, a spreadsheet).
 *
 * pick → reading → review → published, exactly like the monthly statements: the
 * model reads, a person checks every figure against the words it came from and
 * confirms, and only פרסום makes it the client's. Each investment then shows in
 * the portal as a portfolio of its own, and counts in the summary and the policy
 * control. A published upload can be undone.
 */
type Stage = 'pick' | 'reading' | 'review' | 'published';

const STEPS = [
  { id: 'pick', label: 'בחירת דוחות' },
  { id: 'reading', label: 'קריאה' },
  { id: 'review', label: 'בדיקה ואישור' },
  { id: 'published', label: 'פרסום' },
];

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.xlsx,.xls';
const sleep = (ms: number) => new Promise((done) => setTimeout(done, ms));

export function AlternativesCard() {
  const clientId = useRecoilValue(activeClientState) ?? '';
  const bump = useSetRecoilState(dataVersionState);
  const [overview, setOverview] = useState<AltAdminOverview | null>(null);
  const [stage, setStage] = useState<Stage>('pick');
  const [files, setFiles] = useState<File[]>([]);
  const [batch, setBatch] = useState<AltBatchView | null>(null);
  const [progress, setProgress] = useState<AltBatchProgress | null>(null);
  const [result, setResult] = useState<AltPublishResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'publish' | 'discard' | { undo: string } | null>(null);
  const driving = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      setOverview(await altApi.overview(clientId));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [clientId]);

  // A different client in the header: start over for that client.
  useEffect(() => {
    driving.current = null;
    setStage('pick'); setFiles([]); setBatch(null); setProgress(null); setResult(null); setError(null); setOverview(null);
    void load();
  }, [clientId, load]);

  const readAll = async (batchId: string) => {
    driving.current = batchId;
    setStage('reading');
    for (;;) {
      let step;
      try {
        step = await altApi.extractNext(batchId);
      } catch (e) {
        setError((e as Error).message);
        return;
      }
      if (driving.current !== batchId) return;
      setProgress(step);
      if (step.pending === 0 && step.extracting === 0) break;
      if (step.read === null) await sleep(3000);
    }
    try {
      setBatch(await altApi.get(batchId));
      setStage('review');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const { batch_id } = await altApi.create(clientId);
      for (const f of files) await altApi.upload(batch_id, f);
      setFiles([]);
      setBusy(false);
      await readAll(batch_id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      void load();
    }
  };

  const resume = async (batchId: string) => {
    setError(null);
    const b = await altApi.get(batchId);
    setBatch(b);
    if (b.files.some((f) => f.status === 'pending' || f.status === 'extracting')) await readAll(batchId);
    else setStage('review');
  };

  const act = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const onEdit = (fileId: string, edit: AltFileEdit, confirm = false) =>
    act(async () => { setBatch(await altApi.edit(batch!.batch_id, fileId, edit, confirm)); });
  const onRemove = (fileId: string) =>
    act(async () => { await altApi.remove(fileId); setBatch(await altApi.get(batch!.batch_id)); });

  if (!clientId) return <Card><p className="text-sm text-muted-foreground">בחרו לקוח בסרגל העליון.</p></Card>;

  return (
    <div className="grid grid-cols-1 gap-6">
      <Card>
        <div className="mb-5"><FlowSteps steps={STEPS} current={stage} /></div>

        {stage === 'pick' && (
          <div className="grid grid-cols-1 gap-4">
            <p className="text-sm text-muted-foreground">
              העלו דוחות רבעוניים של השקעות אלטרנטיביות — PDF, תמונה או סריקה, או קובץ אקסל. כל דוח נקרא בנפרד; שום דבר לא
              מגיע ללקוח לפני שתבדקו ותאשרו כל ערך ותפרסמו.
            </p>
            {overview?.open_batch && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm">
                <span>יש קליטה פתוחה שלא פורסמה.</span>
                <button type="button" disabled={busy} onClick={() => void act(() => resume(overview.open_batch!))}
                  className="rounded-md bg-primary px-3 py-1 font-semibold text-primary-foreground disabled:opacity-50">המשך</button>
                <button type="button" disabled={busy}
                  onClick={() => void act(async () => { await altApi.discard(overview.open_batch!); await load(); })}
                  className="rounded-md border border-border px-3 py-1 disabled:opacity-50">מחיקה</button>
              </div>
            )}
            <label className="block">
              <span className="sr-only">בחירת דוחות</span>
              <input type="file" multiple accept={ACCEPT} disabled={busy || !!overview?.open_batch}
                onChange={(e) => setFiles([...(e.target.files ?? [])])}
                className="block w-full text-sm file:me-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-foreground" />
            </label>
            {files.length > 0 && (
              <ul className="grid gap-1 text-sm">
                {files.map((f) => <li key={f.name} className="break-all text-muted-foreground">{f.name}</li>)}
              </ul>
            )}
            <button type="button" disabled={busy || files.length === 0 || !!overview?.open_batch} onClick={() => void start()}
              className="flex w-fit items-center gap-2 rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-50">
              {busy && <Spinner />} קריאת הדוחות
            </button>
          </div>
        )}

        {stage === 'reading' && (
          <div className="flex items-center gap-3 text-sm" role="status">
            <Spinner />
            <span>
              נקראו {progress ? progress.extracted + progress.failed : 0} מתוך {progress?.total ?? '…'}
              {progress?.reading && <> · קורא עכשיו את <bdi>{progress.reading.filename}</bdi> ({progress.reading.elapsed_seconds} שניות)</>}
            </span>
          </div>
        )}

        {stage === 'review' && batch && (
          <div className="grid grid-cols-1 gap-4">
            <AltReview batch={batch} busy={busy} onEdit={(id, e, c) => void onEdit(id, e, c)} onRemove={(id) => void onRemove(id)} />
            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              <button type="button" disabled={busy || !batch.can_publish} onClick={() => setConfirming('publish')}
                className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-50">פרסום ללקוח</button>
              <button type="button" disabled={busy} onClick={() => setConfirming('discard')}
                className="rounded-md border border-border px-4 py-2 text-sm disabled:opacity-50">מחיקת הקליטה</button>
              {!batch.can_publish && <span className="text-sm text-muted-foreground">יש לאשר כל דוח ולתקן את מה שמסומן באדום לפני פרסום.</span>}
            </div>
          </div>
        )}

        {stage === 'published' && result && (
          <div className="grid grid-cols-1 gap-3">
            <p className="font-semibold text-success">פורסם ללקוח.</p>
            <ul className="grid gap-1 text-sm">
              {result.published.map((p) => (
                <li key={p.investment + p.as_of}>
                  <bdi>{p.investment}</bdi> · {fmtDate(p.as_of)} · <span className="tabular-nums">{fmtMoney(p.nav)}</span>
                  {p.replaced && <span className="text-muted-foreground"> (החליף דוח קודם לאותו תאריך)</span>}
                </li>
              ))}
            </ul>
            {result.warnings.map((w) => <Notice key={w.code + w.message} warning={w} />)}
            <button type="button" onClick={() => { setStage('pick'); setResult(null); void load(); }}
              className="w-fit rounded-md border border-border px-4 py-1.5 text-sm">קליטה נוספת</button>
          </div>
        )}

        <ErrorBanner error={error} title="הפעולה נכשלה" />
      </Card>

      <InvestmentsCard overview={overview} busy={busy}
        onToggle={(id, on) => void act(async () => { await altApi.setActive(id, on); await load(); bump((v) => v + 1); })}
        onUndo={(id) => setConfirming({ undo: id })} />

      <ConfirmDialog open={confirming === 'publish'} title="לפרסם ללקוח?" confirmLabel="פרסום"
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          void act(async () => {
            setResult(await altApi.publish(batch!.batch_id));
            setStage('published'); setBatch(null); bump((v) => v + 1); await load();
          });
        }}>
        הערכים שאושרו יופיעו בתיק של הלקוח: כל השקעה כתיק משלה, בסיכום התיק ובבקרת המדיניות. אפשר לבטל אחר כך.
      </ConfirmDialog>
      <ConfirmDialog open={confirming === 'discard'} title="למחוק את הקליטה?" confirmLabel="מחיקה" tone="danger"
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          void act(async () => { await altApi.discard(batch!.batch_id); setBatch(null); setStage('pick'); await load(); });
        }}>
        הדוחות שהועלו ומה שנקרא מהם יימחקו. ללקוח לא השתנה דבר.
      </ConfirmDialog>
      <ConfirmDialog open={typeof confirming === 'object' && confirming !== null} title="לבטל את הפרסום?" confirmLabel="ביטול הפרסום" tone="danger"
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          const id = (confirming as { undo: string }).undo;
          setConfirming(null);
          void act(async () => { await altApi.rollback(id); bump((v) => v + 1); await load(); });
        }}>
        הדוחות שהקליטה הזו פרסמה יוסרו מהתיק של הלקוח, ודוחות קודמים שהיא החליפה יחזרו.
      </ConfirmDialog>
    </div>
  );
}

function InvestmentsCard({ overview, busy, onToggle, onUndo }: {
  overview: AltAdminOverview | null;
  busy: boolean;
  onToggle: (investmentId: string, active: boolean) => void;
  onUndo: (batchId: string) => void;
}) {
  if (!overview) return null;
  return (
    <Card title="השקעות הלקוח">
      {overview.investments.length === 0 ? (
        <p className="text-sm text-muted-foreground">עוד לא פורסמו השקעות אלטרנטיביות ללקוח הזה.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="px-3 py-2 text-start font-normal">השקעה</th>
                <th className="px-3 py-2 text-start font-normal">דוח אחרון</th>
                <th className="px-3 py-2 text-end font-normal">שווי</th>
                <th className="px-3 py-2 text-end font-normal">דוחות</th>
                <th className="px-3 py-2 text-start font-normal">פעילה</th>
              </tr>
            </thead>
            <tbody>
              {overview.investments.map((i) => {
                const last = i.statements.at(-1);
                return (
                  <tr key={i.investment_id} className="border-b border-border/40 last:border-0">
                    <td className="px-3 py-2"><bdi className="font-medium">{i.name}</bdi>{i.manager && <div className="text-xs text-muted-foreground"><bdi>{i.manager}</bdi></div>}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{last ? fmtDate(last.as_of) : '—'}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{last ? fmtMoney(last.nav, i.currency) : '—'}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{i.statements.length}</td>
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={i.is_active} disabled={busy} onChange={(e) => onToggle(i.investment_id, e.target.checked)} />
                        <span className="text-xs text-muted-foreground">{i.is_active ? 'השווי נגרר עד החודש האחרון' : 'נסגרה — לא נגררת'}</span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {overview.batches.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold">קליטות שפורסמו</h3>
          <ul className="grid gap-2 text-sm">
            {overview.batches.map((b) => (
              <li key={b.batch_id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="tabular-nums">{b.published_at ? fmtDate(b.published_at.slice(0, 10)) : '—'}</span>
                <span className="text-muted-foreground">{b.statements} דוחות · {b.published_by}</span>
                {b.status === 'published'
                  ? <button type="button" disabled={busy} onClick={() => onUndo(b.batch_id)} className="rounded-md border border-border px-2.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50">ביטול פרסום</button>
                  : <span className="text-xs text-muted-foreground">בוטלה</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
